// src/utils/rapportMontage.ts — SCE Issues
// Export de la fiche équipement dans les feuilles « Montage et Tests » de
// l'usine (27/09) : un modèle ELEC / AUTOM, un modèle MECA.
//
// ─── Pourquoi remplir LE modèle plutôt que le refaire ───
//
// Les coordinateurs et le BE travaillent déjà dans ces classeurs : logo,
// listes déroulantes (Qui, Famille, sous-famille liée à la famille,
// Acceptation BE…), colonnes BE masquées, coloration des lignes et surtout
// les MACROS — dont le bouton qui verse les remarques acceptées dans la base
// qualité. Un classeur reconstruit perdrait tout ça. On ouvre donc le vrai
// fichier (src/assets/modeles/) et on écrit seulement les valeurs, par
// chirurgie XML : tout le reste du fichier est recopié octet pour octet.
// Remplacer un modèle = remplacer le fichier, sans toucher au code, tant que
// la mise en page (en-tête lignes 1-17, tableau à partir de la ligne 18) tient.
//
// Décisions de Guillaume (27/09) : les problèmes PROCESS ne sont pas exportés,
// les annulés sont exclus, le fichier reste un .xlsm (macros comprises).
//
// Pur : aucun React, aucun réseau. JSZip est chargé à l'export seulement.

import {
  A_QUALIFIER,
  ordonnerBadges,
  type BadgeCloture,
  type Equipement,
  type Issue,
  type Metier,
} from '@/types/issue.types'
import type { ReferenceEDE } from '@/utils/documentationEDE'
import { trigrammeDe } from '@/utils/noms'
import { cellule, chargerJSZip, poserCellule, type ValeurCellule } from '@/utils/xlsxCellules'

export type GabaritRapport = 'ELEC' | 'MECA'

export const GABARITS_RAPPORT: readonly GabaritRapport[] = ['ELEC', 'MECA']

export const LIBELLES_GABARIT: Record<GabaritRapport, string> = {
  ELEC: 'ELEC / AUTOM',
  MECA: 'MECA',
}

const METIERS_GABARIT: Record<GabaritRapport, readonly Metier[]> = {
  ELEC: ['ELEC', 'AUTOM'],
  MECA: ['MECA'],
}

/** Type MIME d'un classeur Excel avec macros. */
export const MIME_XLSM = 'application/vnd.ms-excel.sheet.macroEnabled.12'

// ─── Familles : les libellés EXACTS des listes du modèle ───
//
// La base qualité compte les remarques par libellé : on écrit donc ceux de la
// feuille « Listes » du modèle, fautes et espaces compris (« Améloration »,
// « exessif », l'espace devant « Absence de matériel »), pas ceux de l'appli.
// Un test vérifie que chacun existe bien dans le modèle.
//
// Les familles AUTOM (23/10) n'ont pas d'équivalent dans le modèle : elles
// s'écrivent telles quelles, hors de sa liste déroulante, jusqu'à ce que le
// modèle de l'usine les reçoive.

/** ELEC : famille de l'appli → colonne « Famille » du modèle. */
export const FAMILLES_MODELE_ELEC: Readonly<Record<string, string>> = {
  'Absence de matériel': ' Absence de matériel',
  'Adresse PLC': 'Adresse PLC',
  'Calculs de boucles': 'Calculs de boucles',
  'Configuration appareils': 'Configuration appareils',
  'Évolution matériel': 'Evolution matériel',
  'Raccordement — inversion de fils / mauvais câblage': 'Raccordement : inversion de fils, mauvais cablage',
  'Implantation — élément manquant': "Implantation : élément manquant uniquement dans l'implantation",
  'Implantation — ne se monte pas / non présent': "Implantation : ne se monte pas ou n'est pas présent",
  'Référence matérielle': 'Référence matérielle',
  'Repérage': 'Repérage',
  'Amélioration': 'Améloration ',
}

/** Colonne « Famille » du modèle MECA pour toutes les familles MECA de l'appli. */
export const FAMILLE_MODELE_MECA = 'Erreur conception mécanique'

/** MECA : famille de l'appli → colonne « Sous-famille » du modèle. */
export const SOUS_FAMILLES_MODELE_MECA: Readonly<Record<string, string>> = {
  'Support manquant / mal défini': 'Support manquant /mal défini',
  'Raccord manquant / mal défini': 'Raccord manquant / mal défini',
  'Joint manquant / mal défini': 'Joint manquant / mal défini',
  'Fixation ou vis manquante / mal définie': 'Fixation ou vis manquant / mal défini',
  'Interférence entre pièces': 'Interference entre pièces',
  'Jeu ou frottement excessif': 'Jeu ou frottement exessif',
  'Dimensions incorrectes sur plan': 'Dimensions incorrectes sur plan',
  'Pièce ou composant absent ou mal positionné': 'Pièce ou composant absent ou mal positionné',
  'Matériau non conforme': 'Matériau non conforme',
  'Trous manquants / mal positionnés': 'Trous manquants / mal positionnés',
  'Erreurs TAGS': 'Erreurs TAGS',
  'Article process mal placé': 'Article process mal placé',
  'Erreur tubing': 'Erreur tubing',
}

// ─── Ce qu'on écrit ───

/** Valeur d'une cellule : texte, nombre, ou date (écrite en date Excel). */
export type ValeurRapport = ValeurCellule

/** Cellules à remplir : `{ D2: '4677-H', … }`. Une valeur vide laisse la cellule du modèle. */
export type Cellules = Record<string, ValeurRapport | undefined>

export interface ContenuRapport {
  /** En-tête (lignes 1 à 17) : cellules existantes du modèle. */
  entete: Cellules
  /** Une entrée par ligne du tableau, clés = lettres de colonne (`B`, `C`…). N° posé seul en A. */
  lignes: Cellules[]
}

/**
 * Restriction facultative de l'export (08/10). Les deux critères se cumulent :
 * « non clos » ET « l'un de ces badges ». Sans critère, tout part.
 */
export interface FiltreRapport {
  /** Écarte les clos : ne restent que les problèmes à traiter (à qualifier, en cours). */
  seulementNonClos: boolean
  /** Sémantique « l'un des » ; vide = pas de restriction par badge. */
  badges: ReadonlySet<BadgeCloture>
}

/**
 * Applique le filtre. Pas `correspondAuFiltreBadge` : celui-ci ne retient que les
 * clos, alors qu'un traitement qualité peut être préparé avant la clôture
 * (« NC-S envoyée, pas encore clôturable ») — c'est justement ce qu'on veut
 * pouvoir sortir avec « non clos ».
 */
export function filtrerPourRapport(issues: readonly Issue[], filtre: FiltreRapport): Issue[] {
  return issues.filter((i) => {
    if (filtre.seulementNonClos && i.statut === 'Clos') return false
    if (filtre.badges.size > 0 && !i.badgesCloture?.some((b) => filtre.badges.has(b))) return false
    return true
  })
}

/** Les problèmes qui vont dans le rapport, du plus ancien au plus récent. */
export function problemesDuRapport(issues: readonly Issue[], gabarit: GabaritRapport): Issue[] {
  return issues
    .filter((i) => METIERS_GABARIT[gabarit].includes(i.metier) && i.statut !== 'Annulé')
    .sort((a, b) => a.dateDeclaration.localeCompare(b.dateDeclaration))
}

/**
 * Tout le contenu du rapport, cellule par cellule.
 * @param refEDE schéma électrique le plus récent de l'équipement (ELEC).
 */
export function construireRapport(
  gabarit: GabaritRapport,
  equipement: Equipement,
  issues: readonly Issue[],
  refEDE: ReferenceEDE | undefined,
  aujourdhui: Date,
): ContenuRapport {
  const entete: Cellules = {
    D1: equipement.familleEquipement || equipement.designation,
    D2: equipement.numeroSerie,
    D3: equipement.cve,
    D4: equipement.of,
    D5: equipement.pn,
    D6: equipement.chefProjet,
    C16: new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate()),
  }
  if (gabarit === 'ELEC' && refEDE) {
    entete.H16 = refEDE.numero
    entete.H17 = refEDE.rev
  }

  const lignes = problemesDuRapport(issues, gabarit).map((i): Cellules => {
    const famille = i.famille === A_QUALIFIER ? '' : i.famille
    const commun: Cellules = {
      B: trigrammeDe(i.declarantNom || i.declarant),
      D: remarque(i),
      H: commentaires(i, gabarit === 'ELEC' ? refEDE : undefined),
    }
    if (gabarit === 'ELEC') {
      return {
        ...commun,
        C: i.pageEDE,
        F: famille ? (FAMILLES_MODELE_ELEC[famille] ?? famille) : '',
        G: i.statut === 'Clos' ? 'OUI' : 'NON',
      }
    }
    // MECA : les familles de l'appli sont les sous-familles de « Erreur
    // conception mécanique ». L'ancienne famille unique garde sa place.
    const sousFamille = famille === FAMILLE_MODELE_MECA ? '' : famille ? (SOUS_FAMILLES_MODELE_MECA[famille] ?? famille) : ''
    return {
      ...commun,
      C: i.numeroPlan,
      F: famille ? FAMILLE_MODELE_MECA : '',
      G: sousFamille,
    }
  })

  return { entete, lignes }
}

/** « Titre » puis la description, si elle dit autre chose. */
function remarque(i: Issue): string {
  const titre = i.titre.trim()
  const description = i.description.trim()
  return description && description !== titre ? `${titre}\n${description}` : titre
}

/**
 * N° du problème (pour le retrouver dans l'appli), statut, références qualité
 * et commentaire de fin. L'EDE est rappelé s'il diffère de celui de l'en-tête.
 */
function commentaires(i: Issue, refEDE: ReferenceEDE | undefined): string {
  const statut = i.statut === 'Clos' && i.dateCloture ? `Clos le ${dateCourte(i.dateCloture)}` : i.statut
  const lignes = [`${i.id} — ${statut}`]
  const refs = [i.refFnct, i.refNcs].filter(Boolean)
  if (refs.length > 0) lignes.push(refs.join(' · '))
  else if (i.badgesCloture?.length) lignes.push(ordonnerBadges(i.badgesCloture).join(' · '))
  if (i.numeroEDE && (i.numeroEDE !== refEDE?.numero || (i.revEDE ?? '') !== refEDE.rev)) {
    lignes.push(`EDE ${i.numeroEDE}${i.revEDE ? ` Rev ${i.revEDE}` : ''}`)
  }
  if (i.commentaireCloture?.trim()) lignes.push(i.commentaireCloture.trim())
  return lignes.join('\n')
}

function dateCourte(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`
}

/** `Montage et Tests ELEC - 4677-H - 2026-09-27.xlsm` */
export function nomFichierRapport(gabarit: GabaritRapport, numeroSerie: string, jour: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0')
  const date = `${jour.getFullYear()}-${p(jour.getMonth() + 1)}-${p(jour.getDate())}`
  const ns = numeroSerie.replace(/[\\/:*?"<>|]/g, '_').trim()
  return `Montage et Tests ${gabarit} - ${ns} - ${date}.xlsm`
}

// ─── Écriture dans le modèle ───

/** Ligne du modèle qui porte les en-têtes du tableau ; la suivante sert de gabarit de ligne. */
const LIGNE_ENTETES = 18
const LIGNE_GABARIT = LIGNE_ENTETES + 1

const FEUILLE = 'xl/worksheets/sheet1.xml'
const TABLEAU = 'xl/tables/table1.xml'
const CLASSEUR = 'xl/workbook.xml'

/**
 * Remplit le modèle et rend le classeur. Le modèle n'est pas modifié.
 * @throws Error si le modèle n'a plus la forme attendue (cellule, ligne ou tableau introuvable).
 */
export async function remplirModele(modele: ArrayBuffer | Uint8Array, contenu: ContenuRapport): Promise<Blob> {
  const JSZip = await chargerJSZip()
  const zip = await JSZip.loadAsync(modele)

  const lire = async (chemin: string): Promise<string> => {
    const f = zip.file(chemin)
    if (!f) throw new Error(`Modèle incomplet : ${chemin} introuvable`)
    return f.async('string')
  }

  const n = Math.max(contenu.lignes.length, 1)
  const derniere = LIGNE_ENTETES + n

  let feuille = await lire(FEUILLE)
  for (const [ref, v] of Object.entries(contenu.entete)) feuille = poserCellule(feuille, ref, v)
  feuille = remplirTableau(feuille, contenu.lignes)
  feuille = etendrePlages(feuille, derniere)
    .replace(/(<dimension ref="A1:[A-Z]+)\d+("\/>)/, `$1${derniere}$2`)
  zip.file(FEUILLE, feuille)

  // Le tableau Excel suit les lignes. Le modèle a été enregistré filtré sur
  // « Acceptation = Oui » : filtre retiré, sinon l'icône laisse croire à une
  // liste incomplète.
  const tableau = (await lire(TABLEAU))
    .replace(new RegExp(String.raw`(ref="A${LIGNE_ENTETES}:[A-Z]+)${LIGNE_GABARIT}"`, 'g'), `$1${derniere}"`)
    .replace(/<filterColumn[\s\S]*?<\/filterColumn>/g, '')
    .replace(/<autoFilter ([^>]*)><\/autoFilter>/, '<autoFilter $1/>')
  zip.file(TABLEAU, tableau)

  // Zone d'impression : jusqu'à la dernière ligne.
  const classeur = (await lire(CLASSEUR)).replace(
    new RegExp(String.raw`(<definedName name="_xlnm\.Print_Area"[^>]*>[^<]*\$)${LIGNE_GABARIT}<`),
    `$1${derniere}<`,
  )
  zip.file(CLASSEUR, classeur)

  const octets = await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' })
  return new Blob([octets], { type: MIME_XLSM })
}

/** La ligne gabarit (19) est remplacée par une ligne par problème, mêmes styles colonne par colonne. */
function remplirTableau(xml: string, lignes: readonly Cellules[]): string {
  const motif = new RegExp(String.raw`<row r="${LIGNE_GABARIT}"([^>]*)>([\s\S]*?)</row>`)
  const gabarit = motif.exec(xml)
  if (!gabarit) throw new Error(`Modèle modifié : ligne ${LIGNE_GABARIT} introuvable`)
  const attributs = gabarit[1] ?? ''
  const colonnes = [...(gabarit[2] ?? '').matchAll(new RegExp(String.raw`<c r="([A-Z]+)${LIGNE_GABARIT}"((?:\s+[\w:]+="[^"]*")*)`, 'g'))]
    .map((m) => ({ col: m[1] ?? '', style: /\ss="(\d+)"/.exec(m[2] ?? '')?.[1] }))

  const aRemplir = lignes.length > 0 ? lignes : [{}]
  const rangs = aRemplir.map((valeurs, k) => {
    const r = LIGNE_GABARIT + k
    const cellules = colonnes.map(({ col, style }) =>
      cellule(`${col}${r}`, style, col === 'A' ? k + 1 : valeurs[col]),
    )
    return `<row r="${r}"${attributs}>${cellules.join('')}</row>`
  })
  return xml.replace(motif, () => rangs.join(''))
}

/**
 * Listes déroulantes et mises en forme conditionnelles posées sur la ligne
 * gabarit : étendues à toutes les lignes (`E19` → `E19:E25`, `B19:H19` →
 * `B19:H25`). Les formules relatives (`INDIRECT(…F19…)`) suivent seules.
 */
function etendrePlages(xml: string, derniere: number): string {
  const g = LIGNE_GABARIT
  const etendre = (plages: string): string =>
    plages
      .split(' ')
      .map((p) => {
        const seule = new RegExp(String.raw`^([A-Z]+)${g}$`).exec(p)
        if (seule) return `${seule[1]}${g}:${seule[1]}${derniere}`
        const ligne = new RegExp(String.raw`^([A-Z]+)${g}:([A-Z]+)${g}$`).exec(p)
        if (ligne) return `${ligne[1]}${g}:${ligne[2]}${derniere}`
        return p
      })
      .join(' ')
  return xml
    .replace(/sqref="([^"]*)"/g, (_, p: string) => `sqref="${etendre(p)}"`)
    .replace(/<xm:sqref>([^<]*)<\/xm:sqref>/g, (_, p: string) => `<xm:sqref>${etendre(p)}</xm:sqref>`)
}
